import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { EventLogPeriod, EventLogState } from './types';
import susbsidyReportsApiService from 'src/api/services/susbsidyReportsApiService';
import { Paginated, SubsidyEventJournalVM } from 'src/api/contracts';
import { AppState } from 'src/store/store';

const initialState: EventLogState = {
    requestId: undefined,
    status: 'initial',
    period: null,
    items: [],
    total: 0,
    pagination: {
        pageNumber: 1,
        pageSize: 100
    }
};

export const loadEventLog = createAsyncThunk<Paginated<SubsidyEventJournalVM>, void, { state: AppState }>(
    'event-log/load',
    async (_, { getState }) => {
        const state = getState();
        const period = selectEventLogPeriod(state);
        if (!period || !period.dateStart || !period.dateEnd) {
            throw new Error('Не задан период');
        }
        const pagination = selectEventLogPagination(state);
        return await susbsidyReportsApiService.loadEvents({
            filter: {
                dateStart: period.dateStart.format('YYYY-MM-DD'),
                dateEnd: period.dateEnd.format('YYYY-MM-DD')
            },
            pagination
        });
    }
);

export const loadEventLogNextPage = createAsyncThunk<
    Paginated<SubsidyEventJournalVM>,
    {
        pageNumber: number;
        pageSize: number;
    },
    { state: AppState }
>('event-log/load-next-page', async (pagination, { getState }) => {
    const state = getState();
    const period = selectEventLogPeriod(state);
    if (!period || !period.dateStart || !period.dateEnd) {
        throw new Error('Не задан период');
    }
    return await susbsidyReportsApiService.loadEvents({
        filter: {
            dateStart: period.dateStart.format('YYYY-MM-DD'),
            dateEnd: period.dateEnd.format('YYYY-MM-DD')
        },
        pagination
    });
});

const slice = createSlice({
    name: 'event-log',
    initialState,
    reducers: {
        reset: () => initialState,
        periodChanged: (state, action: PayloadAction<EventLogPeriod>) => {
            state.period = action.payload;
            state.items = [];
            state.total = 0;
            state.pagination.pageNumber = 1;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(loadEventLog.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(loadEventLog.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                }
            })
            .addCase(loadEventLog.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = [];
                    state.total = 0;
                    state.pagination.pageNumber = 1;
                }
            })
            .addCase(loadEventLogNextPage.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.pagination.pageNumber = action.meta.arg.pageNumber;
                state.pagination.pageSize = action.meta.arg.pageSize;
            })
            .addCase(loadEventLogNextPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                    state.pagination.pageNumber = action.payload.pageNumber;
                }
            })
            .addCase(loadEventLogNextPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = [];
                    state.total = 0;
                    state.pagination.pageNumber = 1;
                }
            });
    }
});

export const { reset, periodChanged } = slice.actions;

const selectEventLog = (state: AppState) => state.eventLog;

export const selectEventLogStatus = (state: AppState) => selectEventLog(state).status;

export const selectEventLogPeriod = (state: AppState) => selectEventLog(state).period;

export const selectEventLogItems = (state: AppState) => selectEventLog(state).items;

export const selectEventLogTotal = (state: AppState) => selectEventLog(state).total;

export const selectEventLogPagination = (state: AppState) => selectEventLog(state).pagination;

export const selectEventLogCanLoadData = (state: AppState) => {
    const period = selectEventLogPeriod(state);
    return Boolean(period && period.dateStart && period.dateEnd);
};

export const selectEventLogIsLoading = (state: AppState) => selectEventLogStatus(state) === 'loading';

export default slice.reducer;
